import {
  type ConditionalOperator,
  compareFieldCondition,
  isValueRequiredOperator,
} from "./conditional-display";
import {
  CUSTOM_FIELD_SOURCE_PREFIX,
  GOAL_TYPE_SOURCE,
  isProductAssessmentSource,
  isQuizScoreSource,
  PRODUCT_ASSESSMENT_SOURCE_PREFIX,
  QUIZ_SCORE_SOURCE_PREFIX,
} from "./conditional-sources";
import {
  isPresenceOnlyCustomFieldType,
  isUnorderedFieldType,
  parseMultiselectValue,
} from "./field-types";
import type {
  ProductAssessmentExperienceResult,
  ProductAssessmentOutcome,
  ProductAssessmentResult,
  QuizComponentResult,
} from "./types/flow-instances";
import {
  type AssessmentModeStatus,
  classifyOutcomeStatus,
  combineModeStatuses,
} from "./types/product-assessment-verdict";
import type {
  ConfiguredAssessmentType,
  ProductTypeAssessmentMode,
} from "./types/product-assessments";
import { readQuizScore } from "./types/quizzes";

/**
 * QL-361 — product-assessment conditional-display sources.
 *
 * `__pa_<productTypeId>_{quiz|questionnaire|combined}_status` and
 * `__pa_<productTypeId>_{quiz|questionnaire}_score`, one flat sentinel per
 * source so the "one source, one operator, one value" condition
 * model is unchanged. Pure and free of I/O: every evaluator (advisor wizard,
 * public flow, completion engine, PDF) delegates here so they cannot drift.
 *
 * Status is derived clock-free from the stored record. The stored per-mode
 * `status` (a function of the clock at save time) and the stored per-type
 * `verdict` (absent on older records) are deliberately NOT read, and neither
 * is the widget's live attempt-availability classification.
 *
 * Score is the stored outcome score as-is, in points: the last completed
 * attempt for a quiz, the full sum for a questionnaire. Combined has no score —
 * the two are never added together. No score means a score rule never matches
 * (`matchScoreCondition`); null is never treated as zero.
 *
 * QL-541 — `__pa_<productTypeId>_simple_status` is the Simple source of a
 * simple (declaration-only) type: one with neither a quiz nor a questionnaire.
 * It reads the type's top-level `hasExperience` as "true" / "false" (the group
 * aggregate for a member-mode group); no record or no entry is "false". A type
 * has either the simple mode or assessment modes, never both. No score.
 *
 * QL-698 — a group answered per member (`answeringMode: "members"`) reads its
 * status sources from `memberResults[]`, never from the aggregate (whose
 * outcomes carry no score). Each current member gets the single-client status
 * and the members are AND-ed: any failed → failed, all passed → passed, else
 * unanswered / incomplete. A group with no members reads unanswered / incomplete.
 * Its score sources read a range `{ min, max }` over the current members' scores
 * (a single client: min = max). Any member unscored or external, or no members,
 * reads no score. The matcher compares the range, so a rule matches only when
 * every member's score satisfies it.
 */

export type AssessmentSourceMode = "quiz" | "questionnaire" | "combined" | "simple";

/** The modes that carry a stored outcome (and a score source). */
type OutcomeMode = Exclude<AssessmentSourceMode, "combined" | "simple">;
export type AssessmentSourceMeasure = "status" | "score";

export type ParsedProductAssessmentSource = {
  productTypeId: string;
  mode: AssessmentSourceMode;
  measure: AssessmentSourceMeasure;
};

/** Stored values of a single-mode status source. Same strings as the flow quiz. */
export const ASSESSMENT_MODE_STATUSES = ["passed", "failed", "unanswered"] as const;
export type AssessmentModeSourceStatus = (typeof ASSESSMENT_MODE_STATUSES)[number];

/** Stored values of the combined status source. */
export const ASSESSMENT_COMBINED_STATUSES = ["passed", "failed", "incomplete"] as const;
export type AssessmentCombinedSourceStatus = (typeof ASSESSMENT_COMBINED_STATUSES)[number];

/** Stored values of the Simple source — the ticked checkbox, as a string. */
export const ASSESSMENT_SIMPLE_STATUSES = ["true", "false"] as const;
export type AssessmentSimpleSourceStatus = (typeof ASSESSMENT_SIMPLE_STATUSES)[number];

export type AssessmentSourceStatus =
  | AssessmentModeSourceStatus
  | AssessmentCombinedSourceStatus
  | AssessmentSimpleSourceStatus;

const MODES: readonly AssessmentSourceMode[] = ["quiz", "questionnaire", "combined", "simple"];

/** Only quiz and questionnaire have a score source. */
export function hasScoreSource(mode: AssessmentSourceMode): mode is OutcomeMode {
  return mode === "quiz" || mode === "questionnaire";
}

export function buildProductAssessmentStatusSource(
  productTypeId: string,
  mode: AssessmentSourceMode,
): string {
  return `${PRODUCT_ASSESSMENT_SOURCE_PREFIX}${productTypeId}_${mode}_status`;
}

/** Combined and simple have no score source — the two scores stay separate. */
export function buildProductAssessmentScoreSource(
  productTypeId: string,
  mode: OutcomeMode,
): string {
  return `${PRODUCT_ASSESSMENT_SOURCE_PREFIX}${productTypeId}_${mode}_score`;
}

/**
 * The PDF field-value key of a source: `__pa_<…>` -> `pa_<…>`, the sentinel
 * minus its leading underscores. The PDF remap and the field builder's seeding
 * both go through here, so they cannot disagree on the key.
 */
export function productAssessmentSourceFieldKey(sourceFieldId: string): string {
  return isProductAssessmentSource(sourceFieldId) ? sourceFieldId.slice(2) : sourceFieldId;
}

/**
 * Splits a source id into its parts. The suffix is matched from the end, so a
 * product type id may itself contain underscores. Null for anything malformed.
 */
export function parseProductAssessmentSource(
  sourceFieldId: string,
): ParsedProductAssessmentSource | null {
  if (!isProductAssessmentSource(sourceFieldId)) return null;
  const rest = sourceFieldId.slice(PRODUCT_ASSESSMENT_SOURCE_PREFIX.length);
  for (const mode of MODES) {
    for (const measure of ["status", "score"] as const) {
      // `_combined_score` / `_simple_score` are not sources: malformed, like any
      // other unknown suffix.
      if (measure === "score" && !hasScoreSource(mode)) continue;
      const suffix = `_${mode}_${measure}`;
      if (!rest.endsWith(suffix)) continue;
      const productTypeId = rest.slice(0, -suffix.length);
      return productTypeId ? { productTypeId, mode, measure } : null;
    }
  }
  return null;
}

/** The slice of a configured assessment type the source helpers read. */
export type ConfiguredModes = Pick<ConfiguredAssessmentType, "id" | "quiz" | "questionnaire">;

function isModeConfigured(type: ConfiguredModes, mode: AssessmentSourceMode): boolean {
  if (mode === "quiz") return type.quiz != null;
  if (mode === "questionnaire") return type.questionnaire != null;
  if (mode === "simple") return type.quiz == null && type.questionnaire == null;
  return type.quiz != null && type.questionnaire != null;
}

/**
 * The modes offered for one configured type — Combined only when it has both,
 * Simple only when it has neither (so never alongside another mode).
 */
export function configuredAssessmentSourceModes(type: ConfiguredModes): AssessmentSourceMode[] {
  return MODES.filter((mode) => isModeConfigured(type, mode));
}

/** The parsed source when its type is in the list and has the mode configured, else null. */
function resolveConfiguredSource(
  sourceFieldId: string,
  configuredTypes: ConfiguredModes[],
): ParsedProductAssessmentSource | null {
  const parsed = parseProductAssessmentSource(sourceFieldId);
  if (!parsed) return null;
  const type = configuredTypes.find((t) => t.id === parsed.productTypeId);
  return type && isModeConfigured(type, parsed.mode) ? parsed : null;
}

/**
 * True when a rule names a product type or mode that is not configured on the
 * flow's collection any more — the type was deleted or deactivated, the mode
 * was switched off (removing the quiz also takes Combined with it), or the flow
 * points at another collection or none. Nothing rewrites such a rule, so the
 * save-time guard refuses it and the pickers warn about it; at runtime the
 * reader below leaves it unresolved. `configuredTypes` holds active types only.
 * False for anything that is not a product-assessment source.
 */
export function isDanglingProductAssessmentSource(
  sourceFieldId: string,
  configuredTypes: ConfiguredModes[],
): boolean {
  if (!isProductAssessmentSource(sourceFieldId)) return false;
  return resolveConfiguredSource(sourceFieldId, configuredTypes) === null;
}

/**
 * Every source id that is valid for the given configured types, in list order:
 * per mode its status, then — quiz and questionnaire only — its score. The flow
 * and PDF seeders walk this list, so a configured source is always seeded.
 */
export function listProductAssessmentSources(configuredTypes: ConfiguredModes[]): string[] {
  return configuredTypes.flatMap((type) =>
    configuredAssessmentSourceModes(type).flatMap((mode) =>
      hasScoreSource(mode)
        ? [
            buildProductAssessmentStatusSource(type.id, mode),
            buildProductAssessmentScoreSource(type.id, mode),
          ]
        : [buildProductAssessmentStatusSource(type.id, mode)],
    ),
  );
}

/**
 * Value options of a status source — combined swaps "unanswered" for
 * "incomplete", Simple is "true" / "false". A score source
 * compares against a number and has none.
 */
export function productAssessmentSourceStatuses(
  sourceFieldId: string,
): readonly AssessmentSourceStatus[] {
  const parsed = parseProductAssessmentSource(sourceFieldId);
  if (parsed?.measure === "score") return [];
  if (parsed?.mode === "simple") return ASSESSMENT_SIMPLE_STATUSES;
  return parsed?.mode === "combined" ? ASSESSMENT_COMBINED_STATUSES : ASSESSMENT_MODE_STATUSES;
}

/** True for every source that compares a number: `__pa_…_score` and `__quiz_score_<id>`. */
export function isScoreConditionSource(sourceFieldId: string): boolean {
  return (
    isQuizScoreSource(sourceFieldId) ||
    parseProductAssessmentSource(sourceFieldId)?.measure === "score"
  );
}

/** The only operators a score source offers — "has the client answered" is a status question. */
export const SCORE_SOURCE_OPERATORS: readonly ConditionalOperator[] = [
  "equals",
  "greater_than",
  "less_than",
];

/**
 * Whether a score source accepts `operator`. The pickers call it to reset an
 * operator the new source cannot take; the evaluators to refuse one.
 */
export function isScoreSourceOperator(operator: string): boolean {
  return (SCORE_SOURCE_OPERATORS as readonly string[]).includes(operator);
}

/** True for a product-assessment status source (`__pa_…_status`). */
export function isAssessmentStatusSource(sourceFieldId: string): boolean {
  return parseProductAssessmentSource(sourceFieldId)?.measure === "status";
}

/**
 * The only operators a product-assessment status source offers — a status is
 * always resolved to a value, so Is empty / Is not empty would be constant.
 */
export const STATUS_SOURCE_OPERATORS: readonly ConditionalOperator[] = ["equals", "not_equals"];

function isStatusSourceOperator(operator: string): boolean {
  return (STATUS_SOURCE_OPERATORS as readonly string[]).includes(operator);
}

/**
 * Why a saved rule is malformed, or null when it is well formed: a score or
 * product-assessment status source with an operator its menu does not offer,
 * or — for any source — a comparing operator (isValueRequiredOperator) with no value, or
 * a set operator (In / Not in) with nothing selected (QL-120), or a
 * product-assessment status rule whose value is not one of the source's
 * statuses (case-insensitive, like the evaluators compare it). The pickers
 * prevent all of these; the write-side guard refuses them for every other caller
 * (AI tools, stale clients), because such a rule never matches
 * and the element would be hidden for every client.
 */
export function malformedRuleProblem(
  sourceFieldId: string,
  operator: string,
  conditionValue: string,
  fieldType = "",
):
  | "score_operator"
  | "status_operator"
  | "presence_operator"
  | "ordering_operator"
  | "selection"
  | "value"
  | "status_value"
  | null {
  if (isScoreConditionSource(sourceFieldId) && !isScoreSourceOperator(operator)) {
    return "score_operator";
  }
  if (isAssessmentStatusSource(sourceFieldId) && !isStatusSourceOperator(operator)) {
    return "status_operator";
  }
  // QL-254 / QL-237 — the pickers' menus, enforced for every caller. Only
  // checked when the caller knows the source's field type.
  if (
    isPresenceOnlySource(sourceFieldId, fieldType) &&
    operator !== "is_empty" &&
    operator !== "is_not_empty"
  ) {
    return "presence_operator";
  }
  if (
    isUnorderedFieldType(fieldType) &&
    (operator === "greater_than" || operator === "less_than")
  ) {
    return "ordering_operator";
  }
  if (
    (operator === "in" || operator === "not_in") &&
    parseMultiselectValue(conditionValue).length === 0
  ) {
    return "selection";
  }
  if (isValueRequiredOperator(operator) && !conditionValue.trim()) return "value";
  if (
    isAssessmentStatusSource(sourceFieldId) &&
    !(productAssessmentSourceStatuses(sourceFieldId) as readonly string[]).includes(
      conditionValue.toLowerCase(),
    )
  ) {
    return "status_value";
  }
  return null;
}

/**
 * The operator a picker keeps when the author switches to a score source: the
 * current one if a score takes it, otherwise Equals.
 */
export function scoreSourceOperator<T extends string>(operator: T): T | "equals" {
  return isScoreSourceOperator(operator) ? operator : "equals";
}

/**
 * The operator a condition picker keeps when the author switches to a new
 * source, so a leftover operator the new source's menu hides is never saved
 * as a rule that can never be true. `fieldType` is the new source's field
 * type ("" for a sentinel or an unknown source).
 *
 * - goal type takes the set operators only (QL-120) → `in`;
 * - a score keeps Equals / Greater than / Less than (QL-361);
 * - a product-assessment status keeps Equals / Not equals (QL-361);
 * - a multiselect answer is a set → a membership operator (or the empty pair);
 * - a presence-only client custom field keeps the empty pair → `is_empty` (QL-254);
 * - every other source is scalar: membership and set operators become Equals,
 *   and so does an ordering operator on an enum source (a sentinel, or a field
 *   whose type has no order, see `isUnorderedFieldType`), whose menu has no
 *   ordering.
 */
export function operatorForNewSource(
  operator: ConditionalOperator,
  newSource: { sourceId: string; fieldType: string },
): ConditionalOperator {
  const { sourceId, fieldType } = newSource;
  if (sourceId === GOAL_TYPE_SOURCE) return "in";
  if (isScoreConditionSource(sourceId)) return scoreSourceOperator(operator);
  if (isAssessmentStatusSource(sourceId)) {
    return isStatusSourceOperator(operator) ? operator : "equals";
  }
  if (fieldType === "multiselect") {
    const keeps =
      operator === "contains" ||
      operator === "not_contains" ||
      operator === "is_empty" ||
      operator === "is_not_empty";
    return keeps ? operator : "contains";
  }
  if (isPresenceOnlySource(sourceId, fieldType)) {
    return operator === "is_not_empty" ? operator : "is_empty";
  }
  const isMembershipOp = operator === "contains" || operator === "not_contains";
  const isSetOp = operator === "in" || operator === "not_in";
  const isOrderingOp = operator === "greater_than" || operator === "less_than";
  // A client custom field is a `__` sentinel too, but a number one keeps its order.
  const isEnum =
    (sourceId.startsWith("__") && !sourceId.startsWith(CUSTOM_FIELD_SOURCE_PREFIX)) ||
    isUnorderedFieldType(fieldType);
  return isMembershipOp || isSetOp || (isEnum && isOrderingOp) ? "equals" : operator;
}

/**
 * QL-237 / QL-254 — the operator a picker shows, and saves, for a stored rule.
 * A rule saved before its source's menu narrowed can carry an operator the
 * menu no longer lists, which renders a blank Select and which the server now
 * refuses. Such a rule gets the operator a switch to its source would pick.
 * `menu` is the picker's scalar menu (`scalarConditionOperators`).
 */
export function menuConditionOperator(
  operator: ConditionalOperator,
  menu: readonly ConditionalOperator[],
  source: { sourceId: string; fieldType: string },
): ConditionalOperator {
  return menu.includes(operator) ? operator : operatorForNewSource(operator, source);
}

/** QL-254 — a client custom text, textarea or date field: presence only. */
function isPresenceOnlySource(sourceId: string, fieldType: string): boolean {
  return (
    sourceId.startsWith(CUSTOM_FIELD_SOURCE_PREFIX) && isPresenceOnlyCustomFieldType(fieldType)
  );
}

/** Menu of a presence-only source (QL-254). */
const PRESENCE_SOURCE_OPERATORS: readonly ConditionalOperator[] = ["is_empty", "is_not_empty"];

/** Menu of an enum source (fixed values, no ordering). */
const ENUM_SOURCE_OPERATORS: readonly ConditionalOperator[] = [
  "equals",
  "not_equals",
  "is_empty",
  "is_not_empty",
];

/** Menu of an ordered source (number, date, numeric sentinels). */
const ORDERED_SOURCE_OPERATORS: readonly ConditionalOperator[] = [
  "equals",
  "not_equals",
  "greater_than",
  "less_than",
  "is_empty",
  "is_not_empty",
];

/**
 * The operator menu of a condition picker for a scalar source (anything but a
 * multiselect answer or the goal-type source). A score source gets
 * SCORE_SOURCE_OPERATORS and a product-assessment status source
 * STATUS_SOURCE_OPERATORS; both win over `isEnumSource`, so a picker's enum
 * list may cover a whole sentinel family (`__…`, `__pa_…`) without carving
 * them out. An enum source has no ordering, and neither does a field whose
 * `fieldType` is text, email, phone, an option type or boolean (QL-237). A
 * client custom text, textarea or date field gets Is Empty / Is Not Empty
 * only (QL-254).
 */
export function scalarConditionOperators(
  sourceFieldId: string,
  isEnumSource: boolean,
  fieldType = "",
): readonly ConditionalOperator[] {
  if (isScoreConditionSource(sourceFieldId)) return SCORE_SOURCE_OPERATORS;
  if (isAssessmentStatusSource(sourceFieldId)) return STATUS_SOURCE_OPERATORS;
  if (isPresenceOnlySource(sourceFieldId, fieldType)) return PRESENCE_SOURCE_OPERATORS;
  return isEnumSource || isUnorderedFieldType(fieldType)
    ? ENUM_SOURCE_OPERATORS
    : ORDERED_SOURCE_OPERATORS;
}

/**
 * QL-698 — the lowest and highest score of the subjects a score source reads:
 * one client (min = max) or every current member of a member-mode group.
 */
export type ScoreRange = { min: number; max: number };

/** A single score as a range (min = max); null stays null. */
export function scoreRangeOf(score: number | null): ScoreRange | null {
  return score === null ? null : { min: score, max: score };
}

/**
 * The verdict of a score rule. Every evaluator returns this instead of falling
 * through to the lenient sentinel comparator, which would let `not_equals`,
 * `is_empty` or an empty rule value match a client who has no score. No score
 * never matches, whatever the operator; the comparison itself is numeric.
 *
 * The range makes "every member satisfies the rule" exact for the three score
 * operators: `> v` ⇔ min > v, `< v` ⇔ max < v, `= v` ⇔ min = max = v.
 */
export function matchScoreCondition(
  range: ScoreRange | null,
  operator: ConditionalOperator,
  conditionValue: string,
): boolean {
  if (range === null) return false;
  const compare = (score: number) =>
    compareFieldCondition(String(score), operator, conditionValue, "number");
  if (operator === "greater_than") return compare(range.min);
  if (operator === "less_than") return compare(range.max);
  if (operator === "equals") return compare(range.min) && compare(range.max);
  return false;
}

/**
 * Score range in a string-valued map (public flow form values, PDF field
 * values), as "min,max". "" is "configured, no score" — distinct from an absent
 * key, which means the source is not configured.
 */
export function seedScoreRange(range: ScoreRange | null): string {
  // String() always writes "." as the decimal point, so "," is a safe separator.
  return range === null ? "" : `${range.min},${range.max}`;
}

export function parseSeededScoreRange(seeded: string): ScoreRange | null {
  const parts = seeded.split(",");
  if (parts.length !== 2 || parts.some((p) => p.trim() === "")) return null;
  const [min, max] = parts.map(Number) as [number, number];
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
  return { min, max };
}

function parseRecord(calculatedData: string | null | undefined): ProductAssessmentResult | null {
  if (!calculatedData) return null;
  try {
    return JSON.parse(calculatedData) as ProductAssessmentResult;
  } catch {
    return null;
  }
}

const RECORD_MODE: Record<OutcomeMode, ProductTypeAssessmentMode> = {
  quiz: "QUIZ",
  questionnaire: "QUESTIONNAIRE",
};

/**
 * One mode's status from its stored outcome. An externally recorded result has
 * no score, so its pass flag decides; otherwise passed wins, a score present
 * means failed (whether or not another attempt is possible), else unanswered.
 */
function modeStatus(
  outcome: Pick<ProductAssessmentOutcome, "score" | "passed" | "resultSource"> | undefined,
): AssessmentModeSourceStatus {
  if (!outcome) return "unanswered";
  if (outcome.resultSource === "external") return outcome.passed ? "passed" : "failed";
  const status = classifyOutcomeStatus(outcome);
  return status === "incomplete" ? "unanswered" : status;
}

export type ProductAssessmentSourceRead =
  | { resolved: true; measure: "status"; value: AssessmentSourceStatus }
  /**
   * `scoreRange: null` — configured, but nothing scored (yet, partially, or
   * externally recorded); for a group, any member unscored or no members.
   */
  | { resolved: true; measure: "score"; scoreRange: ScoreRange | null }
  /** The type or mode is not configured — such a rule never matches, whatever the operator. */
  | { resolved: false };

/**
 * Read a source's value from the `product_assessment` component's
 * `calculatedData` blob. A configured type/mode with no stored record reads
 * unanswered / incomplete, so a "Not answered" rule matches a client who never
 * opened the component; a type/mode that is NOT configured is unresolvable.
 * A score source reads the stored number as a range (min = max), or null when
 * there is none. A simple
 * source reads the type's top-level `hasExperience` — the group
 * aggregate for a member-mode group — and "false" when nothing is stored.
 * A status source on a member-mode group is the AND over its current members'
 * own statuses (QL-698); zero members read unanswered / incomplete. Its score
 * is the range over the members' scores, null if any member has none.
 */
export function readProductAssessmentSource(
  sourceFieldId: string,
  calculatedData: string | null | undefined,
  configuredTypes: ConfiguredModes[],
): ProductAssessmentSourceRead {
  const parsed = resolveConfiguredSource(sourceFieldId, configuredTypes);
  if (!parsed) return { resolved: false };

  const record = parseRecord(calculatedData);
  const entry = record?.experiences?.find((e) => e.productTypeId === parsed.productTypeId);
  if (parsed.mode === "simple") {
    // `declaredBy` is not read: an untouched box is the same as an unticked one.
    return {
      resolved: true,
      measure: "status",
      value: entry?.hasExperience === true ? "true" : "false",
    };
  }

  // QL-698 — a group answered per member: the aggregate's outcomes carry no
  // score, so each current member is read like a single client and the members
  // are AND-ed. One entry per subject either way.
  const subjects =
    record?.answeringMode === "members"
      ? (record.memberResults ?? []).map((m) =>
          m.experiences.find((e) => e.productTypeId === parsed.productTypeId),
        )
      : [entry];

  if (parsed.mode !== "combined" && parsed.measure === "score") {
    const mode = parsed.mode;
    const scores = subjects.map((subject) => outcomeScore(outcomeOf(subject, mode)));
    return { resolved: true, measure: "score", scoreRange: rangeOverScores(scores) };
  }
  const modes: OutcomeMode[] =
    parsed.mode === "combined" ? ["quiz", "questionnaire"] : [parsed.mode];
  const statuses = subjects.flatMap((subject) =>
    modes.map((mode) => toVerdictStatus(modeStatus(outcomeOf(subject, mode)))),
  );
  // No members: never AND over an empty list, which would read "passed".
  const verdict = statuses.length === 0 ? "incomplete" : combineModeStatuses(statuses);
  if (parsed.mode === "combined") return { resolved: true, measure: "status", value: verdict };
  return {
    resolved: true,
    measure: "status",
    value: verdict === "incomplete" ? "unanswered" : verdict,
  };
}

/**
 * QL-698 — every configured, resolvable source's value as a string, keyed by
 * its `__pa_…` source id: a status as it is, a score as the seeded range
 * ("min,max", "" for no score). A type or mode that is not configured has no
 * key, so its rules never match. The PDF field builder (`pa_…` keys) and the
 * client-flow condition-values endpoint both go through here, so they cannot
 * drift.
 */
export function resolveProductAssessmentConditionValues(
  calculatedData: string | null | undefined,
  configuredTypes: ConfiguredModes[],
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const sourceId of listProductAssessmentSources(configuredTypes)) {
    const read = readProductAssessmentSource(sourceId, calculatedData, configuredTypes);
    if (!read.resolved) continue;
    values[sourceId] = read.measure === "score" ? seedScoreRange(read.scoreRange) : read.value;
  }
  return values;
}

/**
 * QL-698 — a `__quiz_score_<id>` source's value from the `quiz` component's
 * `calculatedData` blob. A single client reads the last completed attempt's
 * score (min = max). A group answered per member reads `memberResults[]`,
 * never the aggregate in `results`, whose score is always null: the range over
 * the current members, null if any member has no score (never attempted, an
 * external result, an override with no attempt) or there are no members.
 */
export function readQuizScoreRange(
  calculatedData: string | null | undefined,
  quizId: string,
): ScoreRange | null {
  if (!calculatedData || !quizId) return null;
  let record: QuizComponentResult | null;
  try {
    record = JSON.parse(calculatedData) as QuizComponentResult | null;
  } catch {
    return null;
  }
  if (record?.answeringMode !== "members") {
    return scoreRangeOf(readQuizScore(calculatedData, quizId));
  }
  return rangeOverScores(
    (record.memberResults ?? []).map((m) => {
      const score = m.results?.[quizId]?.score;
      return typeof score === "number" && Number.isFinite(score) ? score : null;
    }),
  );
}

/**
 * QL-698 — every listed quiz's `__quiz_score_<id>` value as the seeded range
 * ("min,max", "" for no score), resolved from the full stored blob. The PDF
 * field builder and the client-flow condition-values endpoint both go through
 * here, so they cannot drift.
 */
export function resolveQuizConditionValues(
  calculatedData: string | null | undefined,
  quizIds: readonly string[],
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const quizId of quizIds) {
    values[`${QUIZ_SCORE_SOURCE_PREFIX}${quizId}`] = seedScoreRange(
      readQuizScoreRange(calculatedData, quizId),
    );
  }
  return values;
}

/** The range over every subject's score; no subjects, or any unscored, is no score — never read as 0. */
function rangeOverScores(scores: readonly (number | null)[]): ScoreRange | null {
  if (scores.length === 0 || scores.some((s) => s === null)) return null;
  const numbers = scores as number[];
  return { min: Math.min(...numbers), max: Math.max(...numbers) };
}

function outcomeOf(
  entry: ProductAssessmentExperienceResult | undefined,
  mode: OutcomeMode,
): ProductAssessmentOutcome | undefined {
  return entry?.assessments?.find((o) => o.mode === RECORD_MODE[mode]);
}

/** An external system scores on its own scale — its result carries no number. */
function outcomeScore(outcome: ProductAssessmentOutcome | undefined): number | null {
  if (outcome?.resultSource === "external") return null;
  return outcome?.score ?? null;
}

function toVerdictStatus(status: AssessmentModeSourceStatus): AssessmentModeStatus {
  return status === "unanswered" ? "incomplete" : status;
}
